import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CreditCard, Check, Sparkle, Crown, Star } from "@phosphor-icons/react";

const PLAN_ICONS = {
  starter: Star,
  professional: Sparkle,
  enterprise: Crown,
};

const PLAN_COLORS = {
  starter: "#81B29A",
  professional: "#2F4F4F",
  enterprise: "#3D405B",
};

export default function BillingPage() {
  const [plans, setPlans] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [loadingCheckout, setLoadingCheckout] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [pollStatus, setPollStatus] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/subscription"),
      ]);
      setPlans(p.data);
      setSubscription(s.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Poll status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    setPollStatus("Verifying payment...");
    let attempts = 0;
    const maxAttempts = 5;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPollStatus("Payment successful!");
          toast.success(`${data.plan_id?.toUpperCase()} plan activated`);
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          fetchAll();
          setTimeout(() => setPollStatus(""), 3000);
          return;
        }
        if (data.status === "expired") {
          setPollStatus("Payment session expired.");
          toast.error("Payment expired");
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setPollStatus("Payment status check timed out.");
        }
      } catch {
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else setPollStatus("Error checking status.");
      }
    };
    poll();
  }, [searchParams, setSearchParams, fetchAll]);

  const startCheckout = async (planId) => {
    setLoadingCheckout(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
      setLoadingCheckout("");
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      if (data.url) {
        window.location.href = data.url;
      } else if (data.fallback) {
        window.location.href = data.fallback;
        if (data.message) toast.info(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't open subscription portal");
    }
  };

  const planEntries = Object.entries(plans);
  const currentPlan = subscription?.plan || "free";
  const trialDaysLeft = subscription?.trial_ends ? Math.max(0, Math.ceil((new Date(subscription.trial_ends) - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="billing-page">
      <div className="flex items-center gap-2 mb-6">
        <CreditCard size={24} weight="duotone" style={{ color: "#2F4F4F" }} />
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Billing & Plans</h2>
      </div>

      {pollStatus && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 text-center" data-testid="poll-status">
          <p className="text-sm text-amber-800">{pollStatus}</p>
        </div>
      )}

      {/* Subscription Status */}
      {subscription && (
        <div className={`bg-white rounded-lg border p-5 mb-6 ${subscription.creator_account ? "border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50" : "border-stone-200"}`} data-testid="subscription-status">
          <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Current Subscription</h3>
          {subscription.creator_account ? (
            <div className="flex items-center gap-3 flex-wrap">
              <Crown size={28} weight="duotone" className="text-amber-500" />
              <div>
                <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs" data-testid="subscription-badge">
                  CREATOR · UNLIMITED
                </Badge>
                <p className="text-xs text-stone-600 mt-1">All features unlocked. No billing. Thanks for building this 👑</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4 flex-wrap">
              <Badge className={`text-xs ${subscription.status === "active" ? "bg-emerald-100 text-emerald-700" : subscription.status === "trialing" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"}`} data-testid="subscription-badge">
                {subscription.plan?.toUpperCase()} · {subscription.status}
              </Badge>
              {subscription.status === "trialing" && (
                <p className="text-xs text-stone-500">Trial ends in <span className="font-semibold text-amber-700">{trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""}</span> ({new Date(subscription.trial_ends).toLocaleDateString()})</p>
              )}
              {subscription.status === "active" && subscription.current_period_end && (
                <p className="text-xs text-stone-500">Renews on <span className="font-semibold">{new Date(subscription.current_period_end).toLocaleDateString()}</span></p>
              )}
              {(subscription.status === "active" || subscription.status === "trialing") && (
                <Button variant="outline" size="sm" onClick={openPortal} className="ml-auto rounded-full text-xs h-8" data-testid="manage-subscription-btn">
                  Manage subscription
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {planEntries.map(([planId, plan]) => {
          const Icon = PLAN_ICONS[planId] || Star;
          const color = PLAN_COLORS[planId];
          const isCurrent = currentPlan === planId && subscription?.status === "active";
          const isPopular = planId === "professional";
          return (
            <div
              key={planId}
              className={`relative bg-white rounded-xl border p-6 transition-all ${isPopular ? "border-[#2F4F4F] shadow-md" : "border-stone-200"} ${isCurrent ? "ring-2 ring-emerald-400" : ""}`}
              data-testid={`plan-${planId}`}
            >
              {isPopular && (
                <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#2F4F4F] text-white text-[10px]">MOST POPULAR</Badge>
              )}
              {isCurrent && (
                <Badge className="absolute -top-2 right-4 bg-emerald-500 text-white text-[10px]">CURRENT</Badge>
              )}
              <div className="flex items-center gap-2 mb-3">
                <Icon size={22} weight="duotone" style={{ color }} />
                <h3 className="text-lg font-semibold" style={{ color }}>{plan.name}</h3>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-bold" style={{ color: "#1C1917" }}>${plan.amount.toFixed(0)}</span>
                <span className="text-stone-500 text-sm"> / month</span>
              </div>
              <ul className="space-y-2 mb-5 min-h-[180px]">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-stone-600">
                    <Check size={14} weight="bold" style={{ color }} className="mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => startCheckout(planId)}
                disabled={isCurrent || subscription?.creator_account || loadingCheckout === planId}
                className="w-full"
                style={{ backgroundColor: subscription?.creator_account ? "#A8A29E" : (isCurrent ? "#A8A29E" : color) }}
                data-testid={`checkout-${planId}`}
              >
                {loadingCheckout === planId ? "Loading..." : subscription?.creator_account ? "Already unlocked" : isCurrent ? "Current Plan" : "Subscribe"}
              </Button>
            </div>
          );
        })}
      </div>

      <p className="text-center text-[10px] text-stone-400 mt-6">
        14-day free trial. Cancel anytime. Test mode — payments use test cards.
      </p>
    </div>
  );
}
